const FALLBACK_GUIDANCE = {
    checkId: "",
    title: "Quality issue needs review",
    meaning: "A quality gate check failed and needs human review before this run can ship.",
    requiredFix: "Review the failing check message and address the underlying profile or build issue.",
    recommendedCta: "Review Truth / Proof",
    targetSection: "truth_proof",
    clientReady: false,
};
export const QUALITY_ISSUE_GUIDANCE_BY_CHECK_ID = {
    "truth.content.demo_fixture_copy": {
        checkId: "truth.content.demo_fixture_copy",
        title: "Demo or placeholder media in output",
        meaning: "Demo or placeholder media reached the rendered site because no real photos or proof media exist for the slots that need them.",
        requiredFix: "Add real business photos or confirmed proof media to the profile so rendered output uses authentic assets instead of demo fixtures.",
        recommendedCta: "Copy Client Intake Link / Review Truth & Proof",
        targetSection: "truth_proof",
        clientReady: false,
    },
    "truth.profile.weak_evidence": {
        checkId: "truth.profile.weak_evidence",
        title: "Weak or missing proof evidence",
        meaning: "The business profile has no assets, licenses, certifications, insurance records, or other evidence records to support trust on the site.",
        requiredFix: "Add real media, a license, certification, insurance, portfolio work, or a testimonial stored as an evidence record or asset, or declare a primary evidence type.",
        recommendedCta: "Copy Client Intake Link",
        targetSection: "truth_proof",
        clientReady: false,
    },
    "truth.richness.below_package_minimum": {
        checkId: "truth.richness.below_package_minimum",
        title: "Richness below package minimum",
        meaning: "The business graph richness score is below the minimum required for the requested site package.",
        requiredFix: "Add services, service area coverage, or proof evidence to raise richness, or continue with a smaller package that matches current profile depth.",
        recommendedCta: "Enrich profile / Continue with a smaller package",
        targetSection: "truth_proof",
        clientReady: false,
    },
    "truth.richness.insufficient_for_requested_package": {
        checkId: "truth.richness.insufficient_for_requested_package",
        title: "Insufficient richness for requested package",
        meaning: "The profile does not meet the richness threshold required to build the requested site package at full scope.",
        requiredFix: "Add the required services, service areas, and proof to reach the package richness threshold, or choose the smaller package that was actually built.",
        recommendedCta: "Enrich profile / Continue with a smaller package",
        targetSection: "truth_proof",
        clientReady: false,
    },
    "truth.richness.unconfirmed_evidence_used": {
        checkId: "truth.richness.unconfirmed_evidence_used",
        title: "Unconfirmed import evidence used",
        meaning: "Imported or scanned evidence has not been confirmed and cannot unlock higher packages or richer site output.",
        requiredFix: "Confirm the imported evidence through the import confirmation flow before it can count toward richness or package eligibility.",
        recommendedCta: "Review imports",
        targetSection: "import_confirmation",
        clientReady: false,
    },
    "truth.package.unavailable": {
        checkId: "truth.package.unavailable",
        title: "Requested package unavailable",
        meaning: "The requested site package is not available for this business profile or vertical configuration.",
        requiredFix: "Choose an available site package that matches the business type and profile, or enrich the profile until the desired package becomes eligible.",
        recommendedCta: "Review package selection",
        targetSection: "build",
        clientReady: false,
    },
    "truth.package.route_mismatch": {
        checkId: "truth.package.route_mismatch",
        title: "Package route mismatch",
        meaning: "The built site routes do not match the routes expected for the selected site package.",
        requiredFix: "Align the site package selection with the routes that should be generated, then re-run the build.",
        recommendedCta: "Review package and routes",
        targetSection: "build",
        clientReady: false,
    },
    "truth.package.route_disk_alignment": {
        checkId: "truth.package.route_disk_alignment",
        title: "Package route disk alignment failed",
        meaning: "Rendered route artifacts on disk do not align with the site package route plan.",
        requiredFix: "Re-run generation after confirming the correct package and route plan, or fix missing route artifacts before shipping.",
        recommendedCta: "Re-run build",
        targetSection: "build",
        clientReady: false,
    },
    "truth.package.forbidden_route_links": {
        checkId: "truth.package.forbidden_route_links",
        title: "Forbidden route links detected",
        meaning: "The rendered site contains internal links to routes that are not allowed for the selected package.",
        requiredFix: "Remove or correct navigation links that point to disallowed routes, or adjust the package to include the linked pages.",
        recommendedCta: "Review navigation and package",
        targetSection: "build",
        clientReady: false,
    },
    "truth.profile.needs_basics": {
        checkId: "truth.profile.needs_basics",
        title: "Profile missing basics",
        meaning: "The business profile is missing required basics such as business name, contact method, or primary service information.",
        requiredFix: "Complete the core business profile fields through intake or the truth editor before generation can proceed.",
        recommendedCta: "Copy Client Intake Link",
        targetSection: "intake",
        clientReady: false,
    },
    "truth.content.generic_placeholder": {
        checkId: "truth.content.generic_placeholder",
        title: "Generic placeholder copy detected",
        meaning: "Rendered copy still reads as generic placeholder text rather than business-specific content.",
        requiredFix: "Replace placeholder headings and body copy with specific business language drawn from the profile or intake.",
        recommendedCta: "Review Truth & Proof",
        targetSection: "truth_proof",
        clientReady: false,
    },
    "truth.nav.anchor_on_wrong_page": {
        checkId: "truth.nav.anchor_on_wrong_page",
        title: "Navigation anchor on wrong page",
        meaning: "A navigation anchor or section link points to a target that does not exist on the expected page.",
        requiredFix: "Correct the navigation anchor targets so each link resolves to a section on the intended route.",
        recommendedCta: "Review site navigation",
        targetSection: "review",
        clientReady: false,
    },
    "truth.nav.static_export_links": {
        checkId: "truth.nav.static_export_links",
        title: "Static export link issues",
        meaning: "Navigation links are not compatible with static export routing for the built site package.",
        requiredFix: "Update navigation links to use valid static export paths for the package, then re-run generation.",
        recommendedCta: "Review site navigation",
        targetSection: "build",
        clientReady: false,
    },
    "surface-quality.media-demo-fixture-attr": {
        checkId: "surface-quality.media-demo-fixture-attr",
        title: "Demo fixture attribute on media",
        meaning: "Rendered media elements are tagged as demo fixtures, indicating placeholder assets are present in the output.",
        requiredFix: "Add real business media to the profile so rendered images are backed by confirmed assets instead of demo fixtures.",
        recommendedCta: "Review Truth & Proof",
        targetSection: "truth_proof",
        clientReady: false,
    },
    "surface-quality.language-hygiene": {
        checkId: "surface-quality.language-hygiene",
        title: "Language hygiene issues",
        meaning: "Copy on the rendered site is unclear, generic, or weak for visitor and search understanding.",
        requiredFix: "Rewrite primary headings and service blurbs with plain, specific language tied to the business profile.",
        recommendedCta: "Review Truth & Proof",
        targetSection: "truth_proof",
        clientReady: false,
    },
    "surface-quality.lead-path-reachable": {
        checkId: "surface-quality.lead-path-reachable",
        title: "Lead path not reachable",
        meaning: "Visitors cannot easily find a contact path such as phone, form, or booking link on the rendered site.",
        requiredFix: "Add a visible contact path above the fold and ensure the profile includes working contact details.",
        recommendedCta: "Copy Client Intake Link",
        targetSection: "intake",
        clientReady: false,
    },
    "surface-quality.demo-quality": {
        checkId: "surface-quality.demo-quality",
        title: "Weak trust presentation",
        meaning: "The rendered site lacks sufficient trust signals such as testimonials, credentials, or portfolio examples.",
        requiredFix: "Add real testimonials, certifications, or portfolio examples as evidence records or profile assets.",
        recommendedCta: "Review Truth & Proof",
        targetSection: "truth_proof",
        clientReady: false,
    },
    "surface-quality.claim-evidence": {
        checkId: "surface-quality.claim-evidence",
        title: "Unsupported marketing claims",
        meaning: "Marketing claims on the rendered site may read as unsubstantiated by profile evidence.",
        requiredFix: "Support bold claims with specific evidence records or qualify broad statements to match available proof.",
        recommendedCta: "Review Truth & Proof",
        targetSection: "truth_proof",
        clientReady: false,
    },
};
export function guidanceForCheckId(checkId) {
    const mapped = QUALITY_ISSUE_GUIDANCE_BY_CHECK_ID[checkId];
    if (mapped) {
        return mapped;
    }
    return { ...FALLBACK_GUIDANCE, checkId };
}
//# sourceMappingURL=quality-issue-guidance.js.map